/**
 * PricingFeaturesSection — Reusable collapsible pricing + building features
 *
 * Renders two collapsed-by-default sections:
 *  1. Pricing: pricing_model select, conditional rate fields, transient rate
 *  2. Building Features: construction_year, fire_suppression, security_system
 *
 * Works with any react-hook-form instance whose schema includes pricingFieldsSchema.
 */

import { useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { ChevronDown, DollarSign, Building2 } from 'lucide-react'
import {
  PRICING_MODELS,
  PRICING_MODEL_LABELS,
  type PricingModel,
} from '@/types/hangars'

interface PricingFeaturesSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>
}

export function PricingFeaturesSection({ form }: PricingFeaturesSectionProps) {
  const [pricingOpen, setPricingOpen] = useState(false)
  const [featuresOpen, setFeaturesOpen] = useState(false)

  const watchedPricingModel = form.watch('pricing_model') as PricingModel

  return (
    <>
      {/* Pricing Section */}
      <Collapsible open={pricingOpen} onOpenChange={setPricingOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Pricing
              <span className="text-xs text-muted-foreground font-normal">(optional)</span>
            </h3>
            <ChevronDown className={`h-4 w-4 transition-transform ${pricingOpen ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-4">
          <FormField
            control={form.control}
            name="pricing_model"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pricing Model</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value ?? 'fixed'}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {PRICING_MODELS.map((model) => (
                      <SelectItem key={model} value={model}>
                        {PRICING_MODEL_LABELS[model]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Fixed pricing fields */}
          {watchedPricingModel !== 'per_sqft' && (
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="monthly_rate_private"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Monthly Rate (Private)</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          className="pl-7"
                          {...field}
                          value={field.value != null ? String(field.value) : ''}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="monthly_rate_shared"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Monthly Rate (Shared)</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          className="pl-7"
                          {...field}
                          value={field.value != null ? String(field.value) : ''}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* Per sq ft pricing fields */}
          {watchedPricingModel === 'per_sqft' && (
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="rate_per_sqft_private"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rate / Sq Ft (Private)</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          className="pl-7"
                          {...field}
                          value={field.value != null ? String(field.value) : ''}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="rate_per_sqft_shared"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rate / Sq Ft (Shared)</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          className="pl-7"
                          {...field}
                          value={field.value != null ? String(field.value) : ''}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          <FormField
            control={form.control}
            name="transient_rate_nightly"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Transient Rate (Nightly)</FormLabel>
                <FormControl>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      className="pl-7"
                      {...field}
                      value={field.value != null ? String(field.value) : ''}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CollapsibleContent>
      </Collapsible>

      <Separator />

      {/* Building Features Section */}
      <Collapsible open={featuresOpen} onOpenChange={setFeaturesOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Building Features
              <span className="text-xs text-muted-foreground font-normal">(optional)</span>
            </h3>
            <ChevronDown className={`h-4 w-4 transition-transform ${featuresOpen ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-4">
          <FormField
            control={form.control}
            name="construction_year"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Construction Year</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="1"
                    placeholder="2020"
                    {...field}
                    value={field.value != null ? String(field.value) : ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="fire_suppression"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel className="text-sm">Fire Suppression</FormLabel>
                    <FormDescription className="text-xs">
                      Building has fire suppression system
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="security_system"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel className="text-sm">Security System</FormLabel>
                    <FormDescription className="text-xs">
                      Building has security/alarm system
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </>
  )
}
